export function areAllFilledPropertiesEqual(a, b) {
	const biggerObj = Object.keys(a).length >= Object.keys(b).length ? a : b
	const smallerObj = Object.keys(a).length >= Object.keys(b).length ? b : a
	const testedKeys = []
	for (const [key, value] of Object.entries(biggerObj)) {
		testedKeys.push(key)
		if (key in smallerObj) {
			// if the values are not equal
			if (value !== smallerObj[key]) {
				return false
			}
		} else {
			// there is a specified value that is not in the other object
			if (value !== '' && value !== null && value !== undefined) {
				return false
			}
		}
	}
	// there could still be a key in the smallerObj that we did not check yet and that could be not empty;
	return !Object.keys(smallerObj).some(
		(k) =>
			!testedKeys.includes(k) &&
			smallerObj[k] !== '' &&
			smallerObj[k] !== null &&
			smallerObj[k] !== undefined
	)
}

export function hashString(str: string) {
	// https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript
	let hash = 0,
		i,
		chr
	for (i = 0; i < str.length; i++) {
		chr = str.charCodeAt(i)
		hash = (hash << 5) - hash + chr
		hash |= 0 // Convert to 32bit integer
	}
	return hash.toString()
}

// https://dmitripavlutin.com/how-to-compare-objects-in-javascript/#4-deep-equality
/**
 * snapshot svelte proxy objects once at the outermost boundary so that all
 * recursive comparisons operate on already-plain values without re-snapshotting
 */
export function deepEqual(object1, object2, ignoreProperties: Set<string> = new Set()) {
	const obj1 = isObject(object1) ? $state.snapshot(object1) : object1
	const obj2 = isObject(object2) ? $state.snapshot(object2) : object2
	return _deepEqualPlain(obj1, obj2, ignoreProperties)
}

function _deepEqualPlain(obj1, obj2, ignoreProperties: Set<string>) {
	if (!isObject(obj1) || !isObject(obj2)) {
		return obj1 === obj2
	}
	const keys1 = Object.keys(obj1)
	const keys2 = Object.keys(obj2)
	if (keys1.length !== keys2.length) {
		return false
	}
	for (const key of keys1) {
		if (!ignoreProperties.has(key)) {
			const val1 = obj1[key]
			const val2 = obj2[key]
			const areObjects = isObject(val1) && isObject(val2)
			if (
				(areObjects && !_deepEqualPlain(val1, val2, ignoreProperties)) ||
				(!areObjects && val1 !== val2)
			) {
				return false
			}
		}
	}
	return true
}
function isObject(object) {
	return object != null && typeof object === 'object'
}

export function debounce<T extends (...args: any[]) => any>(func: T, timeout: number = 350) {
	let timeoutId: number
	return (...args: Parameters<T>) => {
		clearTimeout(timeoutId)
		timeoutId = setTimeout(func, timeout, ...args)
	}
}

type FormatBytesOptions = {
	decimals?: number // number of fractional digits
	locale?: string | string[] // e.g. 'en', 'de', undefined for default
}

export function formatBytes(bytes: number, options: FormatBytesOptions = {}): string {
	if (!Number.isFinite(bytes)) return '—'
	const base = 1000
	const { decimals = 1, locale = 'de-DE' } = options
	const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']

	let unitIndex = 0
	while (bytes >= base && unitIndex < units.length - 1) {
		bytes = bytes / base
		unitIndex++
	}

	const formatter = new Intl.NumberFormat(locale, {
		minimumFractionDigits: 0,
		maximumFractionDigits: decimals
	})

	return `${formatter.format(bytes)} ${units[unitIndex]}`
}

/**
 * escape HTML-significant characters so untrusted text is not interpreted as markup
 * (safe for text content and single/double-quoted attribute values)
 */
export function escapeHtml(value: string | undefined): string {
	return (value ?? '')
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
		.replaceAll('"', '&quot;')
		.replaceAll("'", '&#39;')
}

export function formatJsonToPrettyHtml(jsonString: string) {
	try {
		const jsonObj = JSON.parse(jsonString)
		const prettyJson = JSON.stringify(jsonObj, null, 4)

		return escapeHtml(prettyJson).replace(/\n/g, '<br>').replace(/ /g, '&nbsp;')
	} catch (_error) {
		console.warn('error while pretty formatting', _error)
		// not valid json: return the escaped original so consumers rendering via {@html} stay safe
		return escapeHtml(jsonString)
	}
}

// https://stackoverflow.com/a/49833666
export function isBehindOtherElement(element: Element) {
	const boundingRect = element.getBoundingClientRect()
	const left = boundingRect.left + 1
	const right = boundingRect.right - 1
	const top = boundingRect.top + 1
	const bottom = boundingRect.bottom - 1

	if (!element.contains(document.elementFromPoint(left, top))) return true
	if (!element.contains(document.elementFromPoint(right, top))) return true
	if (!element.contains(document.elementFromPoint(left, bottom))) return true
	return !element.contains(document.elementFromPoint(right, bottom))
}
